import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import classNames from 'classnames';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import moment, { Moment } from 'moment';
import { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DateInput, isValidDate } from '../../../sharedComponents/dateHelper';
import { LabelWithTimezone } from '../../../sharedComponents/LabelWithTimezone';

type DatePickerWithLabelProps = {
    labelMessageId: string;
    error: React.ReactNode;
    value: DateInput;
    onChange: (value: Date | string) => void;
    timeZone: string;
    isDisabled: boolean;
    alignRight?: boolean;
};

export const DatePickerWithLabel = ({
    labelMessageId,
    error,
    value,
    onChange,
    timeZone,
    isDisabled,
    alignRight,
}: DatePickerWithLabelProps) => {
    const inputId = useId();
    const intl = useIntl();

    const placeholder = useMemo(
        () => intl.formatMessage({ id: 'outboundPortal.common.chooseDatePlaceholder' }),
        [intl],
    );

    const datePickerValue = isValidDate(value) ? toZonedTime(value, timeZone) : value;

    /* bug in react-datetime: https://github.com/arqex/react-datetime/issues/760

       Setting just the value prop to undefined does not properly clear the date picker state.
       Workaround this problem by having also setting inputProps.value to an empty string.
     */

    return (
        <div
            className={classNames('flex-1-1 max-width-50pct', {
                'form-group has-feedback margin-bottom-25 has-error': !!error,
            })}
        >
            <LabelWithTimezone
                htmlFor={inputId}
                messageId={labelMessageId}
                timeZoneData={{ timeZone, date: isValidDate(value) ? value : undefined }}
            />
            <DatePicker
                inputProps={{
                    id: inputId,
                    disabled: isDisabled,
                    placeholder,
                    ...(datePickerValue === undefined ? { value: '' } : {}),
                }}
                clearableInput
                mandatory={false}
                locale={intl.locale}
                alignRight={alignRight ?? false}
                dropup
                closeOnSelect={false}
                value={datePickerValue}
                onChange={(newValue: Moment | string) => {
                    if (moment.isMoment(newValue)) {
                        onChange(fromZonedTime(newValue.toDate(), timeZone));
                    } else {
                        onChange(newValue);
                    }
                }}
                className={classNames('margin-bottom-0', { 'has-error': !!error })}
            />
            {error}
        </div>
    );
};
